<script lang="ts">
  import { fade } from 'svelte/transition';
  import { getStoresContext, type StoresKey } from "~/stores";
  import { onMount } from 'svelte';
  import { hrefFor, languageLabels } from "./header-utils";

  export let request_store_key: StoresKey;
  export let allNavigationLinks: any[] = [];
  export let secondaryNavigationLinks: any[] = [];
  export let enableSecondaryNavLinks: boolean = true;
  export let enableOverlayMenu: boolean = true;
  export let enableOverlayMenuDesktop: boolean = true;
  export let enableOverlayMenuMobile: boolean = true;
  export let enableOverlayLanguageSelector: boolean = true;
  export let todayHours: string = '';

  const stores_context = getStoresContext(request_store_key);
  const { locale, alternate, reverse_router, site, opening_hours, T } = stores_context;
  $: t = $T;

  $: supportedLanguages = $site?.supportedLanguages || [];

  let isMobileView = false;
  let isOverlayMenuOpen = false;

  const browser = typeof window !== 'undefined';
  let currentPath = '';

  // Reactive viewport detection
  function updateViewport() {
    if (browser) {
      isMobileView = window.innerWidth < 992;
    }
  }

  $: if (browser) {
    currentPath = window.location.pathname;
    updateViewport();
  }

  function toggleMobileMenu() {
    isOverlayMenuOpen = !isOverlayMenuOpen;
  }

  function closeMenu() {
    isOverlayMenuOpen = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isOverlayMenuOpen) {
      closeMenu();
    }

    if ((event.key === 'Enter' || event.key === ' ') && event.target) {
      const target = event.target as HTMLElement;
      
      if (target.classList.contains('overlay-backdrop')) {
        event.preventDefault();
        closeMenu();
      }
    }
  }

  $: if (browser) {
    if (isOverlayMenuOpen) {
      document.documentElement.classList.add('demo2025-menu-open');
    } else {
      document.documentElement.classList.remove('demo2025-menu-open');
    }
  }

  onMount(() => {
    const handleClickOutside = (event: MouseEvent) => {
    };

    const handleResize = () => {
      updateViewport();
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    
    updateViewport();
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleResize);
      if (browser) {
        document.documentElement.classList.remove('demo2025-menu-open');
      }
    };
  });
</script>

<!-- Hamburger button -->
{#if enableOverlayMenu && ((enableOverlayMenuDesktop && !isMobileView) || (enableOverlayMenuMobile && isMobileView))}
  <button 
    class="hamburger" 
    on:click={toggleMobileMenu}
    aria-label="Toggle menu"
    aria-expanded={isOverlayMenuOpen}
  >
    <span class:open={isOverlayMenuOpen}></span>
  </button>
{/if}

{#if isOverlayMenuOpen}
  <!-- Overlay backdrop - only shown on desktop -->
  <button 
    class="overlay-backdrop" 
    on:click={closeMenu} 
    aria-label="Close menu"
    transition:fade={{ duration: 150 }}
  ></button>
  
  <!-- Menu with conditional transitions based on viewport size -->
  {#if isMobileView}
    <div class="overlay-menu mobile" transition:fade={{ duration: 150 }}>
      <div class="overlay-menu-content">
        <div class="overlay-nav">
          <!-- Primary Navigation Links  -->
          <ul class="overlay-nav-links">
            {#each allNavigationLinks as link}
              <li class="overlay-nav-item">
                {#if link.isSubmenu}
                  <!-- Submenu item  -->
                  <span class="submenu-title">
                    {link.text}
                  </span>
                {:else}
                  <!-- Regular navigation item -->
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                    target={link.isExternal ? "_blank" : "_self"}
                    rel={link.isExternal ? "noopener noreferrer" : undefined}
                  >
                    {link.text}
                  </a>
                {/if}
                {#if link.children && link.children.length > 0}
                  <ul class="overlay-nav-submenu">
                    {#each link.children as child}
                      <li class="overlay-nav-item submenu-item">
                        {#if child.isSubmenu}
                          <!-- Submenu item  -->
                          <span class="submenu-title">
                            {child.text}
                          </span>
                        {:else}
                          <!-- Regular navigation item -->
                          <a 
                            href={child.href}
                            on:click={() => isOverlayMenuOpen = false}
                            class:active={browser && child.href === currentPath}
                            target={child.isExternal ? "_blank" : "_self"}
                            rel={child.isExternal ? "noopener noreferrer" : undefined}
                          >
                            {child.text}
                          </a>
                        {/if}
                        {#if child.children && child.children.length > 0}
                          <ul class="overlay-nav-submenu level-2">
                            {#each child.children as grandchild}
                              <li class="overlay-nav-item submenu-item level-2">
                                {#if grandchild.isSubmenu}
                                  <!-- Submenu item  -->
                                  <span class="submenu-title">
                                    {grandchild.text}
                                  </span>
                                {:else}
                                  <!-- Regular navigation item -->
                                  <a 
                                    href={grandchild.href}
                                    on:click={() => isOverlayMenuOpen = false}
                                    class:active={browser && grandchild.href === currentPath}
                                    target={grandchild.isExternal ? "_blank" : "_self"}
                                    rel={grandchild.isExternal ? "noopener noreferrer" : undefined}
                                  >
                                    {grandchild.text}
                                  </a>
                                {/if}
                              </li>
                            {/each}
                          </ul>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                {/if}
              </li>
            {/each}
          </ul>
          
          <!-- Secondary Navigation Links -->
          {#if enableSecondaryNavLinks && secondaryNavigationLinks.length > 0}
            <div class="secondary-nav-divider"></div>
            <ul class="overlay-nav-links secondary">
              {#each secondaryNavigationLinks as link}
                <li class="overlay-nav-item secondary">
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                    target={link.isExternal ? "_blank" : "_self"}
                    rel={link.isExternal ? "noopener noreferrer" : undefined}
                  >
                    {link.text}
                  </a>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <div class="overlay-footer">
          <div class="today-hours">
            {t('opening-hours-swiper.todays-hours')}: {todayHours}
          </div>
          {#if enableOverlayLanguageSelector}
            <div class="language-selector">
              {#if supportedLanguages.length > 1}
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-toggle active">
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </span>
                  {:else}
                    <a 
                      class="language-toggle" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={() => isOverlayMenuOpen = false}
                      aria-label={`Switch to ${langCode}`}
                    >
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </a>
                  {/if}
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  {:else}
    <div 
      class="overlay-menu desktop" 
      transition:fade={{ duration: 150 }}
    >
      <div class="overlay-menu-content">
        <!-- Desktop menu content -->
        <div class="overlay-nav">
          <!-- Primary Navigation Links  -->
          <ul class="overlay-nav-links">
            {#each allNavigationLinks as link}
              <li class="overlay-nav-item">
                {#if link.isSubmenu}
                  <!-- Submenu item  -->
                  <span class="submenu-title">
                    {link.text}
                  </span>
                {:else}
                  <!-- Regular navigation item -->
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                    target={link.isExternal ? "_blank" : "_self"}
                    rel={link.isExternal ? "noopener noreferrer" : undefined}
                  >
                    {link.text}
                  </a>
                {/if}
                {#if link.children && link.children.length > 0}
                  <ul class="overlay-nav-submenu">
                    {#each link.children as child}
                      <li class="overlay-nav-item submenu-item">
                        {#if child.isSubmenu}
                          <!-- Submenu item  -->
                          <span class="submenu-title">
                            {child.text}
                          </span>
                        {:else}
                          <!-- Regular navigation item -->
                          <a 
                            href={child.href}
                            on:click={() => isOverlayMenuOpen = false}
                            class:active={browser && child.href === currentPath}
                            target={child.isExternal ? "_blank" : "_self"}
                            rel={child.isExternal ? "noopener noreferrer" : undefined}
                          >
                            {child.text}
                          </a>
                        {/if}
                        {#if child.children && child.children.length > 0}
                          <ul class="overlay-nav-submenu level-2">
                            {#each child.children as grandchild}
                              <li class="overlay-nav-item submenu-item level-2">
                                {#if grandchild.isSubmenu}
                                  <!-- Submenu item  -->
                                  <span class="submenu-title">
                                    {grandchild.text}
                                  </span>
                                {:else}
                                  <!-- Regular navigation item -->
                                  <a 
                                    href={grandchild.href}
                                    on:click={() => isOverlayMenuOpen = false}
                                    class:active={browser && grandchild.href === currentPath}
                                    target={grandchild.isExternal ? "_blank" : "_self"}
                                    rel={grandchild.isExternal ? "noopener noreferrer" : undefined}
                                  >
                                    {grandchild.text}
                                  </a>
                                {/if}
                              </li>
                            {/each}
                          </ul>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                {/if}
              </li>
            {/each}
          </ul>
          
          <!-- Secondary Navigation Links -->
          {#if enableSecondaryNavLinks && secondaryNavigationLinks.length > 0}
            <div class="secondary-nav-divider"></div>
            <ul class="overlay-nav-links secondary">
              {#each secondaryNavigationLinks as link}
                <li class="overlay-nav-item secondary">
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                    target={link.isExternal ? "_blank" : "_self"}
                    rel={link.isExternal ? "noopener noreferrer" : undefined}
                  >
                    {link.text}
                  </a>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <div class="overlay-footer">
          <div class="today-hours">
            {t('opening-hours-swiper.todays-hours')}: {todayHours}
          </div>
          {#if enableOverlayLanguageSelector}
            <div class="language-selector">
              {#if supportedLanguages.length > 1}
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-toggle active">
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </span>
                  {:else}
                    <a 
                      class="language-toggle" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={() => isOverlayMenuOpen = false}
                      aria-label={`Switch to ${langCode}`}
                    >
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </a>
                  {/if}
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
{/if}

<style lang="scss">
  .hamburger {
    background: none;
    border: none;
    padding: 15px;
    padding-right: 0;
    cursor: pointer;
    position: relative;
    z-index: 100;
    
    span {
      display: block;
      width: 14.4px;
      height: 3px;
      background-color: var(--hamburger-icon-color);
      position: relative;
      transition: all 0.3s ease;
      margin-left: auto;

      &::before,
      &::after {
        content: '';
        position: absolute;
        width: 24px;
        height: 3px;
        background-color: var(--hamburger-icon-color);
        right: 0;
        transition: all 0.3s ease;
      }

      &::before {
        top: -8px;
      }

      &::after {
        bottom: -8px;
      }

      &.open {
        background: transparent;

        &::before {
          transform: rotate(45deg);
          top: 0;
          width: 24px;
        }

        &::after {
          transform: rotate(-45deg);
          bottom: 0;
          width: 24px;
        }
      }
    }
  }

  /* Overlay Menu Styles */
  .overlay-backdrop {
    position: fixed;
    top: var(--header-height, 60px);
    left: 0;
    width: 100vw;
    height: calc(100vh - var(--header-height, 60px));
    background: rgba(0, 0, 0, 0.5);
    z-index: 140;
    border: none;
    padding: 0;
    cursor: pointer;
    
    @media screen and (max-width: 991px) {
      display: none; 
    }
  }

  .overlay-menu {
    position: fixed;
    top: var(--header-height, 60px);
    background: var(--overlay-menu-background-color);
    z-index: 150;
    
    &.desktop {
      right: 0;
      width: 50vw;
      height: calc(100vh - var(--header-height, 60px));
      overflow-y: auto;
      transform-origin: right center;
      will-change: transform;
      
      /* Desktop-specific styles */
      .overlay-menu-content {
        height: 100%;
        padding-bottom: 0;
      }
    }
    
    &.mobile {
      width: 100%;
      left: 0;
      height: auto;
      bottom: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: auto;
    }
    
    @media screen and (max-width: 991px) {
      width: 100%;
      left: 0;
    }
  }

  .overlay-menu-content {
    display: flex;
    flex-direction: column;
    padding-bottom: 30px; /* Only for mobile */
  }

  .overlay-nav {
    flex: 1;
    padding: 2rem;
    
    .overlay-nav-links {
      list-style: none;
      padding: 0;
      margin: 0;
      
      .overlay-nav-item {
        margin-bottom: 1.5rem;
        
        &.submenu-item {
          padding-left: 20px;
          
          a {
            font-size: calc(var(--nav-link-font-size, 18px) - 2px);
            font-weight: 500;
          }
        }
        
        &.level-2 {
          padding-left: 40px;
          
          a {
            font-size: calc(var(--nav-link-font-size, 18px) - 4px);
            font-weight: 400;
          }
        }
        
        a {
          display: block;
          text-decoration: none;
          color: var(--overlay-menu-link-color, #424242);
          text-transform: uppercase;
          font-size: var(--nav-link-font-size, 18px);
          font-weight: 600;
          line-height: 30px;
          letter-spacing: 1px;
          transition: color 0.2s ease, opacity 0.2s ease;
          padding: 5px 0;
          white-space: nowrap;

          &.active {
            color: var(--overlay-menu-link-active-color, #424242);
            text-decoration: underline;
            text-underline-offset: 8px;
            text-decoration-thickness: 2px;
          }

          &:hover {
            color: var(--overlay-menu-link-hover-color, #202020);
            opacity: 0.7;
          }
        }

        .submenu-title {
          display: block;
          color: var(--overlay-menu-link-color, #424242);
          text-transform: uppercase;
          font-size: var(--nav-link-font-size, 18px);
          font-weight: 600;
          line-height: 30px;
          letter-spacing: 1px;
          padding: 5px 0;
          white-space: nowrap;
          cursor: default;
        }
      }
      
      .overlay-nav-submenu {
        list-style: none;
        padding: 0;
        margin: 0;
        margin-top: 0.5rem;
        
        &.level-2 {
          margin-top: 0.25rem;
        }
      }
      
      .overlay-nav-item.secondary {
        margin-bottom: 1.5rem;
        
        a {
          font-size: calc(var(--nav-link-font-size, 18px) - 2px);
          font-weight: 500;
          line-height: 24px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--nav-link-color);

          &.active {
            color: var(--nav-link-active-color);
            text-decoration: underline;
            text-underline-offset: var(--nav-link-underline-offset);
            text-decoration-thickness: var(--nav-link-underline-thickness);
            text-decoration-color: var(--nav-link-underline-color);
          }

          &:hover {
            color: var(--nav-link-hover-color);
            opacity: 0.7;
          }
        }
      }
    }
    
    .secondary-nav-divider {
      margin: var(--secondary-nav-divider-vertical-margin, 20px) var(--secondary-nav-divider-horizontal-margin, 0);
      height: var(--secondary-nav-divider-height, 1px);
      background-color: var(--secondary-nav-divider-bg-color, rgba(0, 0, 0, 0.1));
      width: var(--secondary-nav-divider-width, 50px);
      opacity: var(--secondary-nav-divider-opacity, 0.25);
    }

    .overlay-nav-links.secondary {
      margin-top: 10px;
    }
  }

  .overlay-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-top: 1px solid #E5E5E5;
    margin-top: 0.5rem;
    background-color: var(--overlay-menu-background-color);
    color: var(--overlay-menu-link-color, #424242);
    
    .today-hours {
      font-size: 16px;
      letter-spacing: 1px;
      padding-right: 4rem;
      color: inherit;
    }

    .language-selector {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: inherit;
    }

    .language-toggle {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      text-transform: uppercase;
      color: inherit;
      transition: opacity 0.2s ease;
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 0.02em;
      position: relative;
      text-decoration: none;
      display: inline-block;

      &.active {
        font-weight: 600;
        cursor: default;
        
        &::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          background-color: currentColor;
          border-radius: 50%;
        }
      }

      &:hover:not(.active) {
        opacity: 0.7;
      }
    }
  }

  :global(html.demo2025-menu-open) {
    overflow: hidden;
  }
  
  :global(html.demo2025-menu-open body) {
    overflow: hidden;
    position: relative;
  }

  @media screen and (max-width: 991px) {
    .overlay-menu {
      transition: opacity 0.3s ease;
    }
  }
</style>